import { ScenarioData, Listing, calcListingCompensation, calcBuyerAgentCompensation, calcRevenueStamps, calcTotalExpenses, calcNetProceeds, formatCurrency, formatPct } from '@/lib/index';

export function generatePrintHTML(listing: Listing): string {
  const scenarios = listing.scenarios;

  const rows: Array<{ label: string; values: string[]; section?: string; isCalc?: boolean; isTotal?: boolean; isNet?: boolean; isHeader?: boolean }> = [];

  // Helper
  const v = (s: ScenarioData, val: number | string) => typeof val === 'string' ? val : formatCurrency(val);

  // Sales Price
  rows.push({ label: 'Sales Price', values: scenarios.map(s => formatCurrency(s.salesPrice)), section: 'SALES PRICE', isHeader: true });
  rows.push({ label: 'Sales Price', values: scenarios.map(s => formatCurrency(s.salesPrice)) });
  rows.push({ label: 'Settlement Date', values: scenarios.map(s => s.settlementDate || '—') });

  // Pre-marketing
  rows.push({ label: 'PRE-MARKETING EXPENSES', values: ['', '', ''], section: 'PRE-MARKETING EXPENSES', isHeader: true });
  rows.push({ label: 'Appraisal', values: scenarios.map(s => formatCurrency(s.appraisal)) });
  rows.push({ label: 'Inspections (Home, Pest, Radon, Well, Septic, etc.)', values: scenarios.map(s => formatCurrency(s.inspections)) });
  rows.push({ label: 'Repairs or Upfits (Paint, Carpet, Fixtures, etc.)', values: scenarios.map(s => formatCurrency(s.repairsUpfits)) });
  rows.push({ label: 'Other:', values: scenarios.map(s => formatCurrency(s.preMarketingOther1)) });
  rows.push({ label: 'Other:', values: scenarios.map(s => formatCurrency(s.preMarketingOther2)) });

  // Mortgages
  rows.push({ label: 'MORTGAGES AND LIENS', values: ['', '', ''], section: 'MORTGAGES AND LIENS', isHeader: true });
  rows.push({ label: '1st Mortgage Payoff', values: scenarios.map(s => formatCurrency(s.mortgage1)) });
  rows.push({ label: '2nd Mortgage Payoff', values: scenarios.map(s => formatCurrency(s.mortgage2)) });
  rows.push({ label: 'Home Equity Loan', values: scenarios.map(s => formatCurrency(s.homeEquityLoan)) });
  rows.push({ label: 'HOA Assessment(s)', values: scenarios.map(s => formatCurrency(s.hoaAssessment)) });
  rows.push({ label: 'Fixture Liens (solar, windows, HVAC, generator, etc.)', values: scenarios.map(s => formatCurrency(s.fixtureLiens)) });
  rows.push({ label: 'Other Lien:', values: scenarios.map(s => formatCurrency(s.otherLien1)) });
  rows.push({ label: 'Other Lien:', values: scenarios.map(s => formatCurrency(s.otherLien2)) });

  // Closing
  rows.push({ label: 'CLOSING EXPENSES', values: ['', '', ''], section: 'CLOSING EXPENSES', isHeader: true });
  rows.push({ label: 'Attorney Fee/Document Preparation', values: scenarios.map(s => formatCurrency(s.attorneyFee)) });
  rows.push({ label: 'Listing Firm Compensation %', values: scenarios.map(s => formatPct(s.listingCommissionPct)) });
  rows.push({ label: 'Listing Firm Compensation Amount', values: scenarios.map(s => formatCurrency(calcListingCompensation(s.salesPrice, s.listingCommissionPct))), isCalc: true });
  rows.push({ label: 'Listing Firm Compliance/Admin/Brokerage Fee', values: scenarios.map(s => formatCurrency(s.listingComplianceFee)) });
  rows.push({ label: 'Buyer Agent Compensation %', values: scenarios.map(s => formatPct(s.buyerAgentCommissionPct)) });
  rows.push({ label: 'Seller Subagent/Buyer Agent Compensation Amount', values: scenarios.map(s => formatCurrency(calcBuyerAgentCompensation(s.salesPrice, s.buyerAgentCommissionPct))), isCalc: true });
  rows.push({ label: 'Seller-Paid Concessions', values: scenarios.map(s => formatCurrency(s.sellerConcessions)) });
  rows.push({ label: 'Home Warranty', values: scenarios.map(s => formatCurrency(s.homeWarranty)) });
  rows.push({ label: 'Miscellaneous Fees (copies, express, wire, etc.)', values: scenarios.map(s => formatCurrency(s.miscFees)) });
  rows.push({ label: 'Pro-Rated HOA Dues', values: scenarios.map(s => formatCurrency(s.proRatedHoaDues)) });
  rows.push({ label: 'HOA Fees (certification, transfer, etc.)', values: scenarios.map(s => formatCurrency(s.hoaFeesCertification)) });
  rows.push({ label: 'Pro-Rated Taxes', values: scenarios.map(s => formatCurrency(s.proRatedTaxes)) });
  rows.push({ label: 'Recording Fees', values: scenarios.map(s => formatCurrency(s.recordingFees)) });
  rows.push({ label: 'Repairs from Home Inspection', values: scenarios.map(s => formatCurrency(s.repairsFromInspection)) });
  rows.push({ label: 'Revenue Stamps ($1.00 per $500.00)', values: scenarios.map(s => formatCurrency(calcRevenueStamps(s.salesPrice))), isCalc: true });
  rows.push({ label: 'Other:', values: scenarios.map(s => formatCurrency(s.closingOther1)) });
  rows.push({ label: 'Other:', values: scenarios.map(s => formatCurrency(s.closingOther2)) });

  // Totals
  rows.push({ label: 'TOTAL ESTIMATED SELLER EXPENSES', values: scenarios.map(s => formatCurrency(calcTotalExpenses(s))), isTotal: true });
  rows.push({ label: 'Less Due Diligence Fee Received', values: scenarios.map(s => formatCurrency(s.dueDiligenceFeeReceived)) });
  rows.push({ label: 'ESTIMATED NET PROCEEDS TO/FROM SELLER', values: scenarios.map(s => formatCurrency(calcNetProceeds(s))), isNet: true });

  const tableRows = rows.filter(r => !r.isHeader).map(r => {
    const bg = r.isNet ? 'background:#1a2f5e;color:#f5c842;font-weight:700;font-size:15px;' : r.isTotal ? 'background:#edf0f7;font-weight:700;' : r.isCalc ? 'background:#f0f5e0;' : '';
    return `<tr style="${bg}">
      <td style="padding:5px 8px;border:1px solid #d1d5db;">${r.label}</td>
      ${r.values.map(v2 => `<td style="padding:5px 8px;border:1px solid #d1d5db;text-align:right;font-family:monospace;">${v2}</td>`).join('')}
    </tr>`;
  });

  // Group into sections for headers
  const sectionColors: Record<string, string> = {
    'SALES PRICE': '#1a2f5e',
    'PRE-MARKETING EXPENSES': '#2d4a7a',
    'MORTGAGES AND LIENS': '#2d4a7a',
    'CLOSING EXPENSES': '#2d4a7a',
  };

  // Build full table with section headers
  let tableBody = '';
  let currentSection = '';
  for (const r of rows) {
    if (r.isHeader) {
      currentSection = r.label;
      tableBody += `<tr><td colspan="4" style="background:${sectionColors[r.label] ?? '#1a2f5e'};color:white;font-weight:700;padding:7px 10px;border:1px solid #d1d5db;font-size:11px;letter-spacing:0.5px;">${r.label}</td></tr>`;
    } else {
      const bg = r.isNet ? 'background:#1a2f5e;color:#f5c842;font-weight:700;font-size:14px;' : r.isTotal ? 'background:#edf0f7;font-weight:700;' : r.isCalc ? 'background:#f3f7e0;' : '';
      tableBody += `<tr style="${bg}">
        <td style="padding:5px 8px;border:1px solid #d1d5db;font-size:12px;">${r.label}</td>
        ${r.values.map(v2 => `<td style="padding:5px 8px;border:1px solid #d1d5db;text-align:right;font-family:monospace;font-size:12px;">${v2}</td>`).join('')}
      </tr>`;
    }
  }

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Seller Estimated Net Sheet - ${listing.sellerName || 'New Listing'}</title>
  <style>
    body { font-family: 'Inter', Arial, sans-serif; margin: 0; padding: 20px; background: #fff; color: #1a1a2e; }
    @media print { body { padding: 5px; } }
    .header { background: #1a2f5e; color: white; padding: 18px 24px; border-radius: 6px 6px 0 0; margin-bottom: 0; }
    .header h1 { margin: 0; font-size: 20px; letter-spacing: 1px; }
    .header .sub { font-size: 13px; color: #b0c4e0; margin-top: 4px; }
    .info-row { display: flex; gap: 32px; background: #f4f7fd; padding: 12px 24px; border: 1px solid #d1d5db; border-top: none; margin-bottom: 8px; }
    .info-item label { font-size: 11px; color: #6b7280; font-weight: 600; text-transform: uppercase; }
    .info-item p { margin: 2px 0 0; font-size: 14px; font-weight: 600; }
    table { width: 100%; border-collapse: collapse; }
    .col-header { background: #f4f7fd; font-weight: 700; padding: 8px 10px; border: 1px solid #d1d5db; text-align: center; font-size: 13px; color: #1a2f5e; }
    .sig-section { margin-top: 24px; border: 1px solid #d1d5db; border-radius: 6px; padding: 20px 24px; }
    .sig-row { display: flex; gap: 32px; margin-top: 12px; }
    .sig-field { flex: 1; border-bottom: 1px solid #9ca3af; padding-bottom: 4px; }
    .sig-field label { font-size: 10px; color: #6b7280; }
    .disclaimer { margin-top: 16px; font-size: 9px; color: #9ca3af; font-style: italic; text-align: center; padding: 8px; border-top: 1px solid #e5e7eb; }
    .legend { display: flex; gap: 16px; margin-bottom: 8px; font-size: 11px; }
    .legend-item { display: flex; align-items: center; gap: 4px; }
    .legend-dot { width: 12px; height: 12px; border-radius: 2px; }
  </style>
</head>
<body>
  <div class="header">
    <h1>SELLER ESTIMATED NET SHEET</h1>
    <div class="sub">Prepared by Danielle Edwards | Real Estate Professional</div>
  </div>
  <div class="info-row">
    <div class="info-item"><label>Seller(s)</label><p>${listing.sellerName || '—'}</p></div>
    <div class="info-item"><label>Property</label><p>${listing.property || '—'}</p></div>
    <div class="info-item"><label>Date Prepared</label><p>${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p></div>
  </div>
  <div class="legend">
    <span class="legend-item"><span class="legend-dot" style="background:#f3f7e0;border:1px solid #ccc;"></span> Auto-calculated</span>
    <span class="legend-item"><span class="legend-dot" style="background:#edf0f7;border:1px solid #ccc;"></span> Section total</span>
    <span class="legend-item"><span class="legend-dot" style="background:#1a2f5e;border:1px solid #ccc;"></span> Net proceeds</span>
  </div>
  <table>
    <thead>
      <tr>
        <th class="col-header" style="text-align:left;">Description</th>
        <th class="col-header">Scenario 1</th>
        <th class="col-header">Scenario 2</th>
        <th class="col-header">Scenario 3</th>
      </tr>
    </thead>
    <tbody>${tableBody}</tbody>
  </table>
  <div class="sig-section">
    <p style="font-weight:600;margin:0 0 8px;font-size:13px;">Signatures</p>
    <div class="sig-row">
      <div class="sig-field"><label>Seller Signature</label><div style="height:32px;"></div></div>
      <div class="sig-field"><label>Date</label><div style="height:32px;"></div></div>
      <div class="sig-field"><label>Agent Signature</label><div style="height:32px;"></div></div>
      <div class="sig-field"><label>Date</label><div style="height:32px;"></div></div>
    </div>
    <div class="sig-row" style="margin-top:16px;">
      <div class="sig-field"><label>Seller Signature</label><div style="height:32px;"></div></div>
      <div class="sig-field"><label>Date</label><div style="height:32px;"></div></div>
      <div style="flex:2;"></div>
    </div>
  </div>
  <div class="disclaimer">NC REALTORS® MAKES NO REPRESENTATION AS TO THE LEGAL VALIDITY OR ADEQUACY OF ANY PROVISION OF THIS FORM IN ANY SPECIFIC TRANSACTION.</div>
</body>
</html>`;
}
